import { createApp } from 'vue';

import '@/assets/css/fonts.css'; // Font Inter local - dùng làm dự phòng nếu CDN lỗi
import '@/commons/interFontCdn.js'; // Load font Inter qua CDN MISA theo môi trường
import '@/assets/scss/override.scss';
import '@/assets/scss/form.scss';
import '@/assets/css/flexbox.css';
import '@/assets/css/common.css';

// Load branch-specific global SCSS for selected files (main-style, app, scroller, btn, icons)
// Single source of truth: VITE_BRANCH
const __scssBranch = import.meta.env.VITE_BRANCH;
const __branchStyles = ['app', 'scroller', 'btn', 'icons'];
const __allBranchScss = import.meta.glob('/src/assets/scss/branch/*/*.scss');
const __allowedFiles = new Set(__branchStyles.map(n => `${n}.scss`));
const __branchGlob = Object.fromEntries(
  Object.entries(__allBranchScss).filter(([p]) => __allowedFiles.has(p.split('/').pop())),
);

const __SCSS_RETRY_MAX = 3; // Total attempts (1 initial + 2 retries)
const __SCSS_RETRY_DELAY = 1000; // ms

function __loadStyle(name) {
  const branchPath = `/src/assets/scss/branch/${__scssBranch}/${name}.scss`;
  if (!__scssBranch || !__branchGlob[branchPath]) return;

  const attempt = n =>
    __branchGlob[branchPath]().catch(err => {
      if (n < __SCSS_RETRY_MAX) {
        console.warn(
          `[SCSS] "${name}.scss" load failed (attempt ${n}/${__SCSS_RETRY_MAX}), retrying in ${__SCSS_RETRY_DELAY}ms…`,
        );
        return new Promise(res => setTimeout(res, __SCSS_RETRY_DELAY)).then(() => attempt(n + 1));
      }
      console.warn(`[SCSS] "${name}.scss" failed to load after ${__SCSS_RETRY_MAX} attempts.`, err);
    });

  attempt(1);
}

// Load in the requested order
__branchStyles.forEach(__loadStyle);

// Warn and suppress "Unable to preload CSS" errors when CDN is unreachable
window.addEventListener('vite:preloadError', event => {
  console.warn('[Preload] CSS preload failed:', event.payload?.message ?? event.payload);
  event.preventDefault();
});

// Note: Do not auto-load branch index.scss or any other files (to avoid picking up _variables.scss)

import App from './App.vue';
import { createPinia } from 'pinia';
import i18n from '@/i18ns/i18n.js'; // Sử dụng localize
import router from '@/routers/router'; // Khai báo router
import { shortKey } from '@/commons/directives/shortkey.js'; // Phím tắt
import { register } from '@/commons/globalPopup.js'; // Đăng ký Popup

// Khai báo các plugin dùng chung
import { pluginInstall } from '@/commons/globalPlugin.js';
import { useComponents } from '@/commons/globalComponents.js';
import { initFilter, msFormat } from '@/commons/msFormat';

const app = createApp(App);

// Khởi tạo pinia
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';

const pinia = createPinia();

pinia.use(piniaPluginPersistedstate);

app.use(pinia);

//Add extension
import './commons/prototype';

if (window.location.hostname == 'ihosapp.misa.vn' || window.location.hostname == 'igovapp.misa.vn') {
  const THRESHOLDS =  {
    TRACE_SAMPLE_RATE: import.meta.env.VITE_SENTRY_TRACE_SAMPLE_RATE, // Tỷ lệ lấy mẫu vòng 1 
    LCP: import.meta.env.VITE_SENTRY_LCP,               // Ngưỡng tối ưu LCP (ms)
    FCP: import.meta.env.VITE_SENTRY_FCP,               // Ngưỡng tối ưu FCP (ms)
    GOOD_SPAN_KEEP_RATE: import.meta.env.VITE_SENTRY_GOOD_SPAN_KEEP_RATE, // Tỷ lệ GIỮ LẠI SPAN CHI TIẾT của nhóm user nhanh 
    BAD_SPAN_KEEP_RATE: import.meta.env.VITE_SENTRY_BAD_SPAN_KEEP_RATE
  };
  const enabledTrace = import.meta.env.VITE_SENTRY_ENABLE_TRACE === 'true' ?? false;
  console.log('Sentry threshold:',THRESHOLDS);
  console.log('Sentry enabledTrace:',enabledTrace);
  import('@sentry/vue')
    .then((Sentry) => {
      Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        sendDefaultPii: true,
        integrations: [
          Sentry.browserTracingIntegration({
            router,
            enableLongTask: false,
            enableLongAnimationFrame: false,
            enableInp: false,
            idleTimeout: 1000,
            // VÒNG 2: Chỉ giữ lại các span cốt lõi phục vụ debug hiệu năng và API
            beforeEmitSpan: (span) => {
              const allowedOps = [
                'pageload',         // Gốc của trang
                'navigation',       // Gốc của chuyển trang SPA
                'browser.metric',   // BẮT BUỘC GIỮ: Để đo LCP/FCP gốc
                'resource.img',     // Ảnh gây chậm LCP
                'resource.render',  // CSS/Fonts chặn render gây chậm FCP
                'resource.script',  // JS chặn render gây chậm FCP
                'http.client'       // Giữ lại toàn bộ API để debug mạng khi trang chậm
              ];
              if (allowedOps.some(op => span.op?.startsWith(op))) {
                return span;
              }
    
              return undefined;
            }
          }),
        ],
        // VÒNG 1: Lấy mẫu ngẫu nhiên 1% lượng traffic gốc hệ thống để tính P75/P90 chính xác
        tracesSampler: (samplingContext) => {
          const originAttr = samplingContext && samplingContext.attributes && samplingContext.attributes['sentry.origin'];
          const allowSample = originAttr && (originAttr.includes('pageload') || originAttr.includes('navigation'));
          const result = allowSample ? THRESHOLDS.TRACE_SAMPLE_RATE : 0.0;
          enabledTrace && console.log('Sentry.tracesSampler', originAttr, result, samplingContext);
          return result;
        },
        // VÒNG 3: QUYẾT ĐỊNH XỬ LÝ TRANSACTION VÀ SPAN TRƯỚC KHI GỬI
        beforeSendTransaction(event) {
          const op = event?.contexts?.trace?.op;
          enabledTrace && console.log('Sentry.beforeSendTransaction', op, event);
          if (op === 'pageload' || op === 'navigation') {
            const measurements = event.measurements || {};
    
            const lcpValue = measurements['lcp']?.value || 0;
            const fcpValue = measurements['fcp']?.value || 0;
            // BƯỚC 1: LÀM SẠCH DASHBOARD (Chỉ hiển thị LCP và FCP)
            const cleanMeasurements = {};
            if (measurements.lcp) cleanMeasurements.lcp = measurements.lcp;
            if (measurements.fcp) cleanMeasurements.fcp = measurements.fcp;
            event.measurements = cleanMeasurements;
    
            // BƯỚC 2: KIỂM TRA ĐIỀU KIỆN USER CHẬM (VƯỢT NGƯỠNG LCP HOẶC FCP)
            if (lcpValue > THRESHOLDS.LCP || fcpValue > THRESHOLDS.FCP) {
              // USER CHẬM: Giữ nguyên 100% transaction và span chi tiết (bao gồm cả HTTP) để debug
              event.tags = { ...event.tags, 'performance.status': 'bad_metrics' };
              if (!THRESHOLDS.BAD_SPAN_KEEP_RATE) return event;
              if (THRESHOLDS.BAD_SPAN_KEEP_RATE > 0 && Math.random() < THRESHOLDS.BAD_SPAN_KEEP_RATE) {
                // Thuộc 2% may mắn: Giữ nguyên toàn bộ span chi tiết phục vụ đối chiếu
                event.tags['performance.span_status'] = 'full_spans';
              } else {
                // 98% user nhanh còn lại: Xóa sạch mảng span con để tiết kiệm dung lượng payload
                event.spans = [];
              }
              return event;
            } else {
              // USER NHANH (ĐẠT CHUẨN): LUÔN LUÔN GIỮ TRANSACTION để Sentry tính toán P75/P90 chuẩn xác
              event.tags = { ...event.tags, 'performance.status': 'good_metrics' };
              // Xúc xắc ngẫu nhiên xem user nhanh này có thuộc diện 2% được giữ lại span hay không
              if (THRESHOLDS.GOOD_SPAN_KEEP_RATE > 0 && Math.random() < THRESHOLDS.GOOD_SPAN_KEEP_RATE) {
                // Thuộc 2% may mắn: Giữ nguyên toàn bộ span chi tiết phục vụ đối chiếu
                event.tags['performance.span_status'] = 'full_spans';
              } else {
                // 98% user nhanh còn lại: Xóa sạch mảng span con để tiết kiệm dung lượng payload
                event.spans = [];
                event.tags['performance.span_status'] = 'cleared_spans';
              }
    
              return event;
            }
          }
          return null;
        }
      });
      
      console.log('Tích hợp Sentry thành công');
    }).catch((error) => {
      console.error('Không thể tải Sentry', error);
    });
}
else {
  console.warn('Sentry không được khởi tạo vì không phải môi trường prod');
}

app.use(router);

pluginInstall(app);

useComponents(app);

initFilter(app);

app.use(i18n);

app.directive('shortkey', shortKey);

register(app);

import { PromiseDialog } from 'vue3-promise-dialog';

app.use(PromiseDialog);

app.mount('#app');
